// تبدیل ProblemDetails (application/problem+json — error-codes.md §۱) به ApiError یکسان برای UI.

/** کدهای خطایی که پوسته رفتار ویژه برایشان دارد. */
export const ErrorCodes = {
  validation: 'PHX-GEN-0001',
  notFound: 'PHX-GEN-0002',
  forbidden: 'PHX-GEN-0004',
  unauthenticated: 'PHX-GEN-0005',
  featureDisabled: 'PHX-PLT-0403',
  limitReached: 'PHX-PLT-0402',
  tenantReadOnly: 'PHX-PLT-0423',
  membershipStale: 'PHX-IAM-0003',
  noActiveMembership: 'PHX-IAM-0103',
  branchNotAllowed: 'PHX-IAM-0104',
  network: 'PHX-NET-0000',
} as const;

export interface ProblemDetails {
  type?: string | null;
  title?: string | null;
  status?: number | string | null;
  detail?: string | null;
  instance?: string | null;
  code?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  [extension: string]: unknown;
}

const STANDARD_FIELDS = new Set([
  'type',
  'title',
  'status',
  'detail',
  'instance',
  'code',
  'traceId',
  'errors',
]);

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string | undefined;
  readonly traceId: string | undefined;
  /** خطاهای فیلدی (`errors`) با کلید camelCase همان بدنه درخواست. */
  readonly fieldErrors: Readonly<Record<string, readonly string[]>>;
  /** مقادیر سطح ریشه، مثل `feature` (PHX-PLT-0403) یا `limit`/`current`/`max` (PHX-PLT-0402). */
  readonly extensions: Readonly<Record<string, unknown>>;

  constructor(init: {
    status: number;
    code: string;
    title?: string | undefined;
    traceId?: string | undefined;
    fieldErrors?: Record<string, string[]>;
    extensions?: Record<string, unknown>;
  }) {
    super(`${init.code} (${init.status})`);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.traceId = init.traceId;
    this.fieldErrors = init.fieldErrors ?? {};
    this.extensions = init.extensions ?? {};
  }

  /** نام Feature غیرفعال (فقط برای PHX-PLT-0403). */
  get feature(): string | undefined {
    const value = this.extensions['feature'];
    return typeof value === 'string' ? value : undefined;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function asProblem(body: unknown): ProblemDetails | undefined {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return body as ProblemDetails;
  }
  if (typeof body === 'string' && body.trim().startsWith('{')) {
    try {
      return asProblem(JSON.parse(body));
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** ساخت ApiError از وضعیت HTTP و بدنه خطا (شیء یا متن JSON). کد ناموجود ← `PHX-GEN-0{status}` (error-codes.md §۳). */
export function problemToApiError(status: number, body: unknown): ApiError {
  const problem = asProblem(body);
  const extensions: Record<string, unknown> = {};
  if (problem) {
    for (const [key, value] of Object.entries(problem)) {
      if (!STANDARD_FIELDS.has(key)) {
        extensions[key] = value;
      }
    }
  }

  return new ApiError({
    status,
    code: problem?.code ?? `PHX-GEN-0${status}`,
    title: problem?.title ?? undefined,
    traceId: problem?.traceId,
    fieldErrors: problem?.errors ?? {},
    extensions,
  });
}

/** خطای شبکه (عدم دسترسی به سرور). */
export function networkError(cause: unknown): ApiError {
  const error = new ApiError({ status: 0, code: ErrorCodes.network });
  (error as { cause?: unknown }).cause = cause;
  return error;
}
